import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '@stores/translationStore';
import { apiClient } from '@services/api';
import PageHeader from '@components/PageHeader';
import PageSpinner from '@components/PageSpinner';

interface Review {
  staff?: number | null;
  cleanliness?: number | null;
  overall?: number | null;
  text?: string | null;
  submittedAt?: string | null;
}

/** Rate the stay — a post-stay module. Submitting again replaces the earlier review. */
export default function ReviewPage() {
  const { hotelSlug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);

  const [review, setReview] = useState<Review>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    apiClient.get<Review>('/api/v1/guest/post-stay/review')
      .then(({ data }) => { setReview(data); setSubmitted(!!data.submittedAt); })
      .catch(() => { /* no review yet, or none possible — the form still shows */ })
      .finally(() => setLoading(false));
  }, []);

  const home = () => navigate(hotelSlug ? `/${hotelSlug}/welcome` : '/welcome');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!review.overall && !review.staff && !review.cleanliness && !review.text?.trim()) {
      setError(tr('review.empty', 'Please give a rating or a comment.'));
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post<Review>('/api/v1/guest/post-stay/review', review);
      setReview(data);
      setSubmitted(true);
    } catch {
      setError(tr('review.failed', 'Could not send your review. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <PageSpinner />;

  const Stars = ({ field, label }: { field: 'overall' | 'staff' | 'cleanliness'; label: string }) => (
    <div className="mb-3">
      <div className="small text-muted mb-1">{label}</div>
      <div className="d-flex gap-1" role="radiogroup" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={review[field] === n}
            className="btn btn-link p-0" style={{ fontSize: '2rem', lineHeight: 1, textDecoration: 'none' }}
            onClick={() => setReview({ ...review, [field]: n })}>
            {(review[field] ?? 0) >= n ? '★' : '☆'}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <PageHeader
        title={tr('review.title', 'Rate your stay')}
        subtitle={tr('review.subtitle', 'Thank you for staying with us')}
        leading={<button className="btn btn-link text-white p-0" onClick={home} aria-label={tr('common.back', 'Back')}>←</button>}
      />
      <div className="py-3">
        {submitted && (
          <div className="alert alert-success">{tr('review.thanks', 'Thank you! Your review has been sent.')}</div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={submit}>
          <Stars field="overall" label={tr('review.overall', 'Overall')} />
          <Stars field="staff" label={tr('review.staff', 'Staff')} />
          <Stars field="cleanliness" label={tr('review.cleanliness', 'Cleanliness')} />
          <label className="form-label small">{tr('review.comment', 'Anything you would like to tell us?')}</label>
          <textarea className="form-control" rows={4} maxLength={4000} value={review.text ?? ''}
            onChange={(e) => setReview({ ...review, text: e.target.value })} />
          <button type="submit" className="btn btn-primary w-100 mt-4" disabled={busy}>
            {busy ? tr('common.saving', 'Saving…') : submitted ? tr('review.update', 'Update review') : tr('review.send', 'Send review')}
          </button>
        </form>
      </div>
    </div>
  );
}
